"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BranchRouteNotEmptyError = void 0;
exports.getEdgeSource = getEdgeSource;
exports.getEdgeTarget = getEdgeTarget;
exports.getEdgeOutputIndex = getEdgeOutputIndex;
exports.generateStepId = generateStepId;
exports.isLoopStepType = isLoopStepType;
exports.insertStepIntoEdges = insertStepIntoEdges;
exports.removeStepFromEdges = removeStepFromEdges;
exports.wouldInsertLoopInsideLoop = wouldInsertLoopInsideLoop;
exports.syncBranchPortEdges = syncBranchPortEdges;
exports.findRouteSteps = findRouteSteps;
exports.collectReachable = collectReachable;
exports.insertBranchPair = insertBranchPair;
exports.insertLoopStep = insertLoopStep;
exports.removeBranchPair = removeBranchPair;
exports.removeLoopWithBody = removeLoopWithBody;
/**
 * Pure graph changes of a workflow: inserting and removing steps and keeping
 * the edges of branches and loops in shape. The builder applies them
 * to the workflow document and the server applies them for AI tools, so both
 * compute the same structure.
 */
const control_flow_1 = require("./control-flow");
/** A branch route that still holds steps cannot be removed. */
class BranchRouteNotEmptyError extends Error {
    constructor(portId) {
        super(`Cannot remove branch route "${portId}" while it contains workflow steps`);
        this.portId = portId;
        this.name = 'BranchRouteNotEmptyError';
    }
}
exports.BranchRouteNotEmptyError = BranchRouteNotEmptyError;
// =============================================================================
// Exported pure helpers — used by both service and tests
// =============================================================================
/** Extract the source step ID from an edge (handles both field names). */
function getEdgeSource(edge) {
    return edge['sourceStepId'] ?? edge['source'];
}
/** Extract the target step ID from an edge (handles both field names). */
function getEdgeTarget(edge) {
    return edge['targetStepId'] ?? edge['target'];
}
/** Extract the output index from an edge. */
function getEdgeOutputIndex(edge) {
    const sourcePort = edge['sourcePort'];
    const portOutputIndex = sourcePort === 'true' || sourcePort === 'continue'
        ? 0
        : sourcePort === 'false' || sourcePort === 'exit'
            ? 1
            : undefined;
    return portOutputIndex ?? edge['sourceOutputIndex'] ?? 0;
}
/** Generate a unique edge ID. */
function generateEdgeId() {
    return `edge-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
/** Generate a unique step ID with a prefix. */
function generateStepId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}
/** Create a standard edge object. */
function makeEdge(source, target, extra) {
    return {
        id: generateEdgeId(),
        sourceStepId: source,
        targetStepId: target,
        source,
        target,
        ...extra,
    };
}
function edgeTargetInputFields(edge) {
    return {
        ...(edge['targetInput'] != null && { targetInput: edge['targetInput'] }),
        ...(edge['targetInputIndex'] != null && {
            targetInputIndex: edge['targetInputIndex'],
        }),
        ...(edge['targetPort'] != null && { targetPort: edge['targetPort'] }),
    };
}
function edgeSourceOutputFields(edge, outputIndex) {
    return {
        sourceOutputIndex: getEdgeOutputIndex(edge) ?? outputIndex,
        ...(edge['sourcePort'] != null && { sourcePort: edge['sourcePort'] }),
    };
}
function shouldSplitEdge(edge, afterStepId, outputIndex, splitEdgeId, alreadyRewired) {
    if (alreadyRewired)
        return false;
    if (splitEdgeId)
        return edge['id'] === splitEdgeId;
    return (getEdgeSource(edge) === afterStepId &&
        getEdgeOutputIndex(edge) === outputIndex);
}
/** Whether a step type is a loop, with or without the engine prefix. */
function isLoopStepType(type) {
    return type === 'loop' || type === 'swr.loop';
}
function isLoopStep(step) {
    return isLoopStepType(step?.['type']);
}
function collectLoopBody(edges, loopId) {
    const body = new Set();
    const queue = edges
        .filter(edge => {
        if (getEdgeSource(edge) !== loopId)
            return false;
        if (getEdgeTarget(edge) === loopId)
            return false;
        return (edge['sourcePort'] === 'continue' || getEdgeOutputIndex(edge) === 0);
    })
        .map(getEdgeTarget);
    while (queue.length > 0) {
        const id = queue.shift();
        if (id === loopId || body.has(id))
            continue;
        body.add(id);
        for (const edge of edges) {
            if (getEdgeSource(edge) === id && !body.has(getEdgeTarget(edge))) {
                queue.push(getEdgeTarget(edge));
            }
        }
    }
    return body;
}
function resolveInsertionAnchor(edges, afterStepId, outputIndex, splitEdgeId) {
    if (!splitEdgeId)
        return { afterStepId, outputIndex };
    const splitEdge = edges.find(edge => edge['id'] === splitEdgeId);
    if (!splitEdge)
        return { afterStepId, outputIndex };
    return {
        afterStepId: getEdgeSource(splitEdge),
        outputIndex: getEdgeOutputIndex(splitEdge),
    };
}
function edgeMatchesBranchPort(edge, branchId, portId, portIndex) {
    if (getEdgeSource(edge) !== branchId)
        return false;
    const sourcePort = edge['sourcePort'];
    if (sourcePort != null)
        return sourcePort === portId;
    return (typeof portIndex === 'number' && getEdgeOutputIndex(edge) === portIndex);
}
function normalizeBranchType(type) {
    return type === control_flow_1.DEFAULT_BRANCH_TYPE ? control_flow_1.DEFAULT_BRANCH_TYPE : 'branch_xor';
}
function cloneDefaultBranchPorts() {
    return control_flow_1.DEFAULT_BRANCH_PORTS.map(port => ({
        portId: port.portId,
        label: port.label,
        condition: { ...port.condition },
    }));
}
function insertStepIntoEdges(edges, afterStepId, newStepId, outputIndex, splitEdgeId) {
    if (!afterStepId) {
        return [...edges];
    }
    const updatedEdges = [];
    let rewired = false;
    for (const edge of edges) {
        if (shouldSplitEdge(edge, afterStepId, outputIndex, splitEdgeId, rewired)) {
            const targetId = getEdgeTarget(edge);
            updatedEdges.push(makeEdge(afterStepId, newStepId, {
                ...edgeSourceOutputFields(edge, outputIndex),
            }));
            if (targetId) {
                updatedEdges.push(makeEdge(newStepId, targetId, {
                    sourceOutputIndex: 0,
                    ...edgeTargetInputFields(edge),
                }));
            }
            rewired = true;
        }
        else {
            updatedEdges.push(edge);
        }
    }
    if (!rewired) {
        if (splitEdgeId) {
            throw new Error(`Split edge "${splitEdgeId}" not found`);
        }
        updatedEdges.push(makeEdge(afterStepId, newStepId, { sourceOutputIndex: outputIndex }));
    }
    return updatedEdges;
}
function removeStepFromEdges(edges, stepId) {
    const incoming = [];
    const outgoing = [];
    const unrelated = [];
    for (const edge of edges) {
        const sourceId = getEdgeSource(edge);
        const targetId = getEdgeTarget(edge);
        if (sourceId === stepId) {
            outgoing.push(edge);
        }
        else if (targetId === stepId) {
            incoming.push(edge);
        }
        else {
            unrelated.push(edge);
        }
    }
    const reconnected = [];
    for (const inc of incoming) {
        const incSource = getEdgeSource(inc);
        if (outgoing.length > 0) {
            for (const out of outgoing) {
                const outTarget = getEdgeTarget(out);
                if (incSource && outTarget) {
                    reconnected.push(makeEdge(incSource, outTarget, {
                        sourceOutputIndex: getEdgeOutputIndex(inc),
                        ...(inc['sourcePort'] != null && {
                            sourcePort: inc['sourcePort'],
                        }),
                    }));
                }
            }
        }
    }
    return [...unrelated, ...reconnected];
}
function wouldInsertLoopInsideLoop(steps, edges, afterStepId, outputIndex, splitEdgeId) {
    const anchor = resolveInsertionAnchor(edges, afterStepId, outputIndex, splitEdgeId);
    if (!anchor.afterStepId)
        return false;
    const afterStep = steps.find(step => step['id'] === anchor.afterStepId);
    for (const step of steps) {
        if (!isLoopStep(step))
            continue;
        const loopId = step['id'];
        if (!loopId)
            continue;
        if (loopId === anchor.afterStepId && anchor.outputIndex === 0) {
            return true;
        }
        if (collectLoopBody(edges, loopId).has(anchor.afterStepId))
            return true;
    }
    return isLoopStep(afterStep) && anchor.outputIndex === 0;
}
function syncBranchPortEdges(branchId, oldConfig, newConfig, edges) {
    const pairedMergeId = newConfig['pairedMergeId'];
    if (!pairedMergeId)
        return edges;
    const oldPorts = (oldConfig['ports'] ?? []).map(p => p.portId);
    const newPorts = (newConfig['ports'] ?? []).map(p => p.portId);
    const oldPortIndexes = new Map(oldPorts.map((p, index) => [p, index]));
    const addedPorts = newPorts.filter(p => !oldPorts.includes(p));
    const removedPorts = oldPorts.filter(p => !newPorts.includes(p));
    if (addedPorts.length === 0 && removedPorts.length === 0)
        return edges;
    let result = [...edges];
    if (removedPorts.length > 0) {
        for (const portId of removedPorts) {
            const routeSteps = findRouteSteps(edges, branchId, portId, pairedMergeId, oldPortIndexes.get(portId));
            if (routeSteps.size > 0)
                throw new BranchRouteNotEmptyError(portId);
        }
        result = result.filter(e => {
            return !removedPorts.some(portId => edgeMatchesBranchPort(e, branchId, portId, oldPortIndexes.get(portId)));
        });
    }
    for (const portId of addedPorts) {
        result.push(makeEdge(branchId, pairedMergeId, {
            sourcePort: portId,
            sourceOutputIndex: newPorts.indexOf(portId),
        }));
    }
    return result;
}
function findRouteSteps(edges, branchId, portId, mergeId, portIndex) {
    // A port can carry several edges; the route is everything any of them reaches.
    const firstSteps = edges
        .filter(e => edgeMatchesBranchPort(e, branchId, portId, portIndex))
        .map(e => getEdgeTarget(e))
        .filter(stepId => stepId !== mergeId);
    if (firstSteps.length === 0)
        return new Set();
    return collectReachable(edges, firstSteps, new Set([mergeId]));
}
function collectReachable(edges, startIds, stopIds) {
    const visited = new Set();
    const queue = [...startIds];
    while (queue.length > 0) {
        const nid = queue.shift();
        if (stopIds.has(nid) || visited.has(nid))
            continue;
        visited.add(nid);
        for (const edge of edges) {
            if (getEdgeSource(edge) === nid) {
                const target = getEdgeTarget(edge);
                if (!stopIds.has(target) && !visited.has(target)) {
                    queue.push(target);
                }
            }
        }
    }
    return visited;
}
function insertBranchPair(steps, edges, afterStepId, outputIndex, branchType, stepName, splitEdgeId) {
    const ts = Date.now();
    const branchId = `branch-${ts}-${Math.random().toString(36).slice(2, 6)}`;
    const mergeId = `merge-${ts}-${Math.random().toString(36).slice(2, 6)}`;
    const actualType = normalizeBranchType(branchType);
    const branchSlug = (0, control_flow_1.branchSlugForType)(actualType);
    const branchPorts = cloneDefaultBranchPorts();
    const branchStep = {
        id: branchId,
        name: stepName ?? 'Branch',
        type: actualType,
        config: {
            type: actualType,
            condition: '',
            pairedMergeId: mergeId,
            ports: branchPorts,
        },
        inputSlots: (0, control_flow_1.buildBranchInputSlots)(branchSlug),
        configSlots: (0, control_flow_1.buildBranchConfigSlots)({
            slug: branchSlug,
            branchType: actualType,
            ports: branchPorts,
            pairedMergeId: mergeId,
        }),
        outputSlots: (0, control_flow_1.buildBranchOutputSlots)(),
        position: { x: 0, y: 0 },
    };
    const mergeStrategy = (0, control_flow_1.defaultMergeStrategyForBranch)(actualType);
    const mergeCollectMode = 'object';
    const mergeStep = {
        id: mergeId,
        name: 'Merge',
        type: 'merge',
        config: {
            type: 'merge',
            pairedBranchId: branchId,
            strategy: mergeStrategy,
            collectMode: mergeCollectMode,
        },
        inputSlots: (0, control_flow_1.buildMergeInputSlots)(),
        configSlots: (0, control_flow_1.buildMergeConfigSlots)({
            pairedBranchId: branchId,
            strategy: mergeStrategy,
            collectMode: mergeCollectMode,
        }),
        outputSlots: (0, control_flow_1.buildMergeOutputSlots)(),
        position: { x: 0, y: 0 },
    };
    // Find and remove the afterStep→target edge
    let originalTargetId = null;
    let originalSourcePort;
    let originalSourceOutputIndex = outputIndex;
    let originalTargetFields = {};
    const updatedEdges = [];
    let rewired = false;
    for (const edge of edges) {
        if (shouldSplitEdge(edge, afterStepId, outputIndex, splitEdgeId, rewired)) {
            originalTargetId = getEdgeTarget(edge);
            originalSourcePort = edge['sourcePort'];
            originalSourceOutputIndex = getEdgeOutputIndex(edge);
            originalTargetFields = edgeTargetInputFields(edge);
            rewired = true;
        }
        else {
            updatedEdges.push(edge);
        }
    }
    if (splitEdgeId && !rewired) {
        throw new Error(`Split edge "${splitEdgeId}" not found`);
    }
    // afterStep → Branch
    if (afterStepId) {
        updatedEdges.push(makeEdge(afterStepId, branchId, {
            sourceOutputIndex: originalSourceOutputIndex,
            ...(originalSourcePort != null && { sourcePort: originalSourcePort }),
        }));
    }
    // Branch →(true)→ Merge, Branch →(false)→ Merge
    updatedEdges.push(makeEdge(branchId, mergeId, {
        sourcePort: 'true',
        sourceOutputIndex: 0,
    }));
    updatedEdges.push(makeEdge(branchId, mergeId, {
        sourcePort: 'false',
        sourceOutputIndex: 1,
    }));
    // Merge → originalTarget
    if (originalTargetId) {
        updatedEdges.push(makeEdge(mergeId, originalTargetId, originalTargetFields));
    }
    return {
        steps: [...steps, branchStep, mergeStep],
        edges: updatedEdges,
        insertedStepId: branchId,
        insertedStepName: branchStep.name,
        mergeId,
    };
}
function insertLoopStep(steps, edges, afterStepId, outputIndex, config, stepName, splitEdgeId) {
    const loopId = generateStepId('loop');
    const projectedLoopConfig = (0, control_flow_1.projectLoopConfig)(config);
    const loopStep = {
        id: loopId,
        name: stepName ?? 'Loop',
        type: 'loop',
        config: {
            ...(config ?? {}),
            type: 'loop',
            ...projectedLoopConfig,
        },
        inputSlots: (0, control_flow_1.buildLoopInputSlots)(),
        configSlots: (0, control_flow_1.buildLoopConfigSlots)(projectedLoopConfig),
        outputSlots: (0, control_flow_1.buildLoopOutputSlots)(),
        position: { x: 0, y: 0 },
    };
    // Find and remove afterStep→target edge
    let originalTargetId = null;
    let originalSourcePort;
    let originalSourceOutputIndex = outputIndex;
    let originalTargetFields = {};
    const updatedEdges = [];
    let rewired = false;
    for (const edge of edges) {
        if (shouldSplitEdge(edge, afterStepId, outputIndex, splitEdgeId, rewired)) {
            originalTargetId = getEdgeTarget(edge);
            originalSourcePort = edge['sourcePort'];
            originalSourceOutputIndex = getEdgeOutputIndex(edge);
            originalTargetFields = edgeTargetInputFields(edge);
            rewired = true;
        }
        else {
            updatedEdges.push(edge);
        }
    }
    if (splitEdgeId && !rewired) {
        throw new Error(`Split edge "${splitEdgeId}" not found`);
    }
    // afterStep → Loop
    if (afterStepId) {
        updatedEdges.push(makeEdge(afterStepId, loopId, {
            sourceOutputIndex: originalSourceOutputIndex,
            ...(originalSourcePort != null && { sourcePort: originalSourcePort }),
        }));
    }
    // Loop →(exit)→ originalTarget
    if (originalTargetId) {
        updatedEdges.push(makeEdge(loopId, originalTargetId, {
            sourcePort: 'exit',
            sourceOutputIndex: 1,
            ...originalTargetFields,
        }));
    }
    // Loop →(continue)→ Loop (back-edge)
    updatedEdges.push(makeEdge(loopId, loopId, {
        sourcePort: 'continue',
        sourceOutputIndex: 0,
    }));
    return {
        steps: [...steps, loopStep],
        edges: updatedEdges,
        insertedStepId: loopId,
        insertedStepName: loopStep.name,
    };
}
function removeBranchPair(steps, edges, stepId) {
    const removedStep = steps.find(s => s['id'] === stepId);
    if (!removedStep)
        return { steps, edges, removedStepIds: [] };
    const removedType = removedStep['type'];
    const config = removedStep['config'];
    let branchId;
    let mergeId;
    if (control_flow_1.CONTROL_BRANCH_TYPES.has(removedType)) {
        branchId = stepId;
        mergeId = config['pairedMergeId'];
    }
    else {
        mergeId = stepId;
        branchId = config['pairedBranchId'];
    }
    // BFS to find all route nodes between branch and merge
    const branchOutTargets = edges
        .filter(e => getEdgeSource(e) === branchId)
        .map(e => getEdgeTarget(e))
        .filter(id => id !== branchId);
    const routeNodeIds = collectReachable(edges, branchOutTargets, new Set([mergeId]));
    // Remove branch + merge + route steps
    const removeIds = new Set([branchId, mergeId, ...routeNodeIds]);
    const updatedSteps = steps.filter(s => !removeIds.has(s['id']));
    // The entries from outside into the branch and the exits after the merge.
    const entryEdges = edges.filter(e => getEdgeTarget(e) === branchId && !removeIds.has(getEdgeSource(e)));
    const exitEdges = edges.filter(e => getEdgeSource(e) === mergeId && !removeIds.has(getEdgeTarget(e)));
    // Remove all edges involving removed steps
    const updatedEdges = edges.filter(e => {
        const src = getEdgeSource(e);
        const tgt = getEdgeTarget(e);
        return !removeIds.has(src) && !removeIds.has(tgt);
    });
    updatedEdges.push(...bypassEdges(entryEdges, exitEdges));
    return {
        steps: updatedSteps,
        edges: updatedEdges,
        removedStepIds: [...removeIds],
    };
}
/**
 * The edges that take the place of removed steps: every entry joined to
 * every exit, keeping the entry's output and the exit's input.
 */
function bypassEdges(entryEdges, exitEdges) {
    return entryEdges.flatMap(entry => exitEdges.map(exit => makeEdge(getEdgeSource(entry), getEdgeTarget(exit), {
        ...edgeSourceOutputFields(entry, getEdgeOutputIndex(entry)),
        ...edgeTargetInputFields(exit),
    })));
}
function removeLoopWithBody(steps, edges, loopId) {
    // Find body nodes via BFS from continue-port targets
    const continueTargets = edges
        .filter(e => getEdgeSource(e) === loopId &&
        e['sourcePort'] === 'continue' &&
        getEdgeTarget(e) !== loopId)
        .map(e => getEdgeTarget(e));
    const bodyNodeIds = collectReachable(edges, continueTargets, new Set([loopId]));
    // Remove loop + body steps
    const removeIds = new Set([loopId, ...bodyNodeIds]);
    const updatedSteps = steps.filter(s => !removeIds.has(s['id']));
    // The entries from outside; the back edges of the body end at the loop too.
    const entryEdges = edges.filter(e => getEdgeTarget(e) === loopId && !removeIds.has(getEdgeSource(e)));
    const exitEdges = edges.filter(e => getEdgeSource(e) === loopId && e['sourcePort'] === 'exit');
    const updatedEdges = edges.filter(e => {
        const src = getEdgeSource(e);
        const tgt = getEdgeTarget(e);
        return !removeIds.has(src) && !removeIds.has(tgt);
    });
    updatedEdges.push(...bypassEdges(entryEdges, exitEdges));
    return {
        steps: updatedSteps,
        edges: updatedEdges,
        removedStepIds: [...removeIds],
    };
}
